import { PROCESS_STEP_UPDATED, LOADING } from './types';
import { isPublicLogin, isUnauthorized, isSessionTerminated } from './../../../api/util';

export const actionBuilder = (client, router, customPostHooks = {}) => {
  const defaultPostHook = (store, processParams) => {
    store.dispatch('ui/updateUi', processParams.definitionKey, { root: true });
    navigateWithQuery(processParams);
    store.commit(`processStep/${LOADING}`, false, { root: true });
  };

  const getRole = () => (router.currentRoute.query.role);

  /**
   * Navigate to new route. Keep external id and role in query URL.
   */
  const navigateWithQuery = ({ definitionKey, externalId }) => {
    const query = {
      ...router.currentRoute.query,
      externalId,
    };
    router.push({
      name: definitionKey,
      query,
      params: { ignoreBackNavigationProtection: true },
      hash: router.currentRoute.hash,
    }).catch(() => {
    });
  };

  /**
   * Override postHooks here
   */
  const runPostHooks = (store, processParams) => {
    if (customPostHooks[processParams.definitionKey]) {
      customPostHooks[processParams.definitionKey](store, processParams);
    } else {
      store.dispatch('processStep/defaultPostHook', processParams, { root: true });
    }
  };

  const updateProcess = (store, data) => {
    store.commit(PROCESS_STEP_UPDATED, data);
    const { definitionKey } = data.task;
    const { externalId } = store.getters;
    // LOSC can return no task
    // Can't check getter as it might have a task already
    runPostHooks(store, {
      externalId,
      definitionKey: definitionKey || (data.processExists ? 'noTask' : 'noProcess'),
    });
  };

  /* Handle all axios error types here */
  const handleError = async (store, { response, viewHandling = false }) => {
    const { externalId } = store.getters;
    if (isPublicLogin(response)) {
      await store.dispatch('auth/login', { }, { root: true });
    } else if (isSessionTerminated(response)) {
      client.logoutSsoSession(store.rootGetters['ui/language']);
    } else if (isUnauthorized(response)) {
      runPostHooks(store, {
        definitionKey: 'unauthorized',
        externalId,
      });
    } else if (response && response.status === 503) {
      runPostHooks(store, {
        definitionKey: 'maintenance',
        externalId,
      });
    } else if (response && response.status === 451) {
      runPostHooks(store, {
        definitionKey: 'error451',
        externalId,
      });
    } else if (viewHandling) {
      throw new Error();
    } else {
      runPostHooks(store, {
        definitionKey: 'error500',
        externalId,
      });
    }
  };

  const createUpsaleApplication = async (store, { application = {}, variables = {}} = {}) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });

    return client
      .createUpsaleApplication({
        application,
        task: { variables },
      })
      .then(({ data }) => store.dispatch('processStep/updateProcess', data, { root: true }))
      .catch(error => store.dispatch('processStep/handleError', error, { root: true }));
  };

  const createApplication = async (store, { application = {}, variables = {}, hCaptchaToken } = {}) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });

    return client
      .createApplication({
        application,
        task: { variables },
      }, hCaptchaToken)
      .then(({ data }) => store.dispatch('processStep/updateProcess', data, { root: true }))
      .catch(error => store.dispatch('processStep/handleError', error, { root: true }));
  };

  /**
   * Generic submit form to update application processStep
   * - start loading icon
   * - make request
   * - update processStep state
   * - update ui based on new task
   * - route to new page
   * - remove loading icon
   */
  const updateProcessStep = async (store, { application = {}, variables = {}} = {}) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });
    const { externalId, definitionKey } = store.getters;

    return client
      .updateProcessStep(externalId, {
        application,
        task: {
          definitionKey,
          variables,
        },
      })
      .then(({ data }) => store.dispatch('processStep/updateProcess', data, { root: true }))
      .catch(error => store.dispatch('processStep/handleError', error, { root: true }));
  };

  const continueProcess = async (store, externalId) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });

    return client
      .continueProcess(externalId, getRole())
      .then(({ data }) => store.dispatch('processStep/updateProcess', data, { root: true }))
      .catch(error => store.dispatch('processStep/handleError', error, { root: true }));
  };

  const updateApplication = async (store, { application = {}} = {}) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });
    const { externalId } = store.getters;

    return client
      .updateApplication(externalId, { application })
      .then(() => {
        // Expected response is 204
        // LOSC endpoint can add response body, but as it can be anything,
        // then can't handle everything here
        store.commit(PROCESS_STEP_UPDATED, { application });
        store.commit(`processStep/${LOADING}`, false, { root: true });

        return Promise.resolve();
      })
      .catch(error => store.dispatch('processStep/handleError', error, { root: true }));
  };

  // Override when needed
  const appStarted = (store) => {
    const { externalId } = router.currentRoute.query;
    if (externalId) {
      // Attempt to continue if externalId exists in query
      store.dispatch('processStep/continueProcess', externalId, { root: true });
    }
  };

  const uploadDocument = async (store, { file, documentOwner, documentTypes }) => {
    await uploadFilesAndCreateDocuments(store, {
      files: [file],
      documentOwner,
      documentTypes,
    });
  };

  const uploadDocuments = async (store, { files, documentOwner, documentType }) => {
    await uploadFilesAndCreateDocuments(store, {
      files,
      documentOwner,
      documentTypes: [documentType],
    });
  };

  const uploadFilesAndCreateDocuments = async (store, { files, documentOwner, documentTypes }) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });
    const { externalId } = store.getters;

    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('documentOwner', documentOwner);
      documentTypes.map(documentType => formData.append('documentTypes[]', documentType));

      await client.uploadDocument(externalId, formData)
        .catch(({ response }) => {
          store.dispatch('processStep/handleError', {
            response,
            viewHandling: true,
          }, { root: true });
        });
    }
    store.commit(`processStep/${LOADING}`, false, { root: true });
  };

  const annulApplication = async (store, reasons) => {
    store.commit(`processStep/${LOADING}`, true, { root: true });
    const { externalId } = store.getters;

    return client
      .annulApplication(externalId, { reasons })
      .then(() => {
        store.commit(`processStep/${LOADING}`, false, { root: true });

        return Promise.resolve();
      })
      .catch(error => store.dispatch('processStep/handleError', error, { root: true }));
  };

  return {
    appStarted,
    uploadDocument,
    uploadDocuments,
    continueProcess,
    defaultPostHook,
    updateProcess,
    createUpsaleApplication,
    handleError,
    updateProcessStep,
    createApplication,
    updateApplication,
    annulApplication,
  };
};
